import { areaOf } from "./areas";
import { placeKey } from "./city-names";
import { COST_INDEX_BASE, GSA_PER_DIEM_SOURCE, STANDARD_CONUS_MIE, STATE_PER_DIEM_SOURCE } from "./constants";
import { US_STATES } from "./regions";
import { getCountryName } from "@/features/flight-estimator/_logic/iso-country-utils";
import type { CityEstimate, CostIndex } from "./types";

/*
 * Per-city cost index for the estimator's default meal and meeting-space rates.
 *
 * Data: public-domain US government per diem rates for meals and incidentals (M&IE), USD a day.
 * - US: GSA per diem rates for FY2027 (October 1, 2026 to September 30, 2027), from the master
 *   rates file at https://www.gsa.gov/travel/plan-a-trip/per-diem-rates/per-diem-files
 *   (FY2027_PerDiemRates_Validated090126.xlsx). A city outside every listed county gets the
 *   standard CONUS rate, $68.
 * - Elsewhere: US State Department foreign per diem rates (DSSR 925), the table published at
 *   https://allowances.state.gov/web920/per_diem.asp as of October 26, 2026, with effective dates
 *   up to November 1, 2026. A post with seasons uses the season rates weighted by days.
 *
 * Index: the place's M&IE over the FY2027 standard CONUS M&IE ($68), times 100. A place on the
 * standard rate scores 100; Las Vegas ($86) scores 126. Foreign rates use the same base.
 *
 * Country averages: the mean M&IE of every location the source lists for the country (GSA: every
 * non-standard area; State: every post, "Other" included), on the same base.
 */

/** One place's published M&IE rate, under every name the estimator may use for it. */
interface PerDiemPlace {
  names: readonly string[];
  /** ISO alpha-2 country. */
  country: string;
  /** For a US place, the states its per diem area covers ("DC", "VA", "MD" for Washington). */
  states?: readonly string[];
  /** Meals and incidentals, USD a day. */
  mie: number;
}

const PLACES: readonly PerDiemPlace[] = [
  // United States, GSA FY2027
  { names: ["New York", "New York City", "NYC", "Manhattan", "Brooklyn"], country: "US", states: ["NY"], mie: 92 }, // GSA: New York City, NY
  {
    names: ["Los Angeles", "Long Beach", "Anaheim", "Irvine", "Pasadena", "Burbank", "Oxnard", "Ojai"],
    country: "US",
    states: ["CA"],
    mie: 86,
  }, // GSA: Los Angeles, CA
  { names: ["Chicago"], country: "US", states: ["IL"], mie: 92 }, // GSA: Chicago, IL
  { names: ["Dallas"], country: "US", states: ["TX"], mie: 80 }, // GSA: Dallas, TX
  { names: ["Fort Worth"], country: "US", states: ["TX"], mie: 80 }, // GSA: Arlington / Fort Worth / Grapevine, TX
  { names: ["Plano"], country: "US", states: ["TX"], mie: 80 }, // GSA: Plano, TX
  { names: ["Houston"], country: "US", states: ["TX"], mie: 80 }, // GSA: Houston, TX
  { names: ["Atlanta"], country: "US", states: ["GA"], mie: 86 }, // GSA: Atlanta, GA
  {
    names: ["Washington", "Washington DC", "Washington D.C.", "Alexandria", "Bethesda"],
    country: "US",
    states: ["DC", "VA", "MD"],
    mie: 92,
  }, // GSA: District of Columbia, DC
  { names: ["Philadelphia"], country: "US", states: ["PA"], mie: 92 }, // GSA: Philadelphia, PA
  { names: ["Miami", "Miami Beach"], country: "US", states: ["FL"], mie: 92 }, // GSA: Miami, FL
  { names: ["Fort Lauderdale"], country: "US", states: ["FL"], mie: 86 }, // GSA: Fort Lauderdale, FL
  { names: ["Boca Raton", "West Palm Beach"], country: "US", states: ["FL"], mie: 86 }, // GSA: Boca Raton / Delray Beach / Jupiter, FL
  { names: ["Phoenix", "Scottsdale", "Mesa", "Tempe"], country: "US", states: ["AZ"], mie: 86 }, // GSA: Phoenix / Scottsdale, AZ
  { names: ["Boston", "Cambridge"], country: "US", states: ["MA"], mie: 92 }, // GSA: Boston / Cambridge, MA
  { names: ["Riverside", "Palm Springs"], country: "US", states: ["CA"], mie: 86 }, // GSA: Palm Springs, CA
  { names: ["San Bernardino", "Ontario"], country: "US", states: ["CA"], mie: 86 }, // GSA: Barstow / Ontario / Victorville, CA
  { names: ["San Francisco"], country: "US", states: ["CA"], mie: 92 }, // GSA: San Francisco, CA
  { names: ["Oakland", "Berkeley"], country: "US", states: ["CA"], mie: 92 }, // GSA: Oakland, CA
  {
    names: ["San Jose", "Palo Alto", "Mountain View", "Santa Clara", "Sunnyvale"],
    country: "US",
    states: ["CA"],
    mie: 92,
  }, // GSA: Sunnyvale / Palo Alto / San Jose, CA
  { names: ["Half Moon Bay", "San Mateo"], country: "US", states: ["CA"], mie: 86 }, // GSA: San Mateo / Foster City / Belmont, CA
  { names: ["Santa Monica"], country: "US", states: ["CA"], mie: 92 }, // GSA: Santa Monica, CA
  { names: ["Detroit"], country: "US", states: ["MI"], mie: 74 }, // GSA: Detroit, MI
  { names: ["Seattle"], country: "US", states: ["WA"], mie: 92 }, // GSA: Seattle, WA
  { names: ["Tacoma"], country: "US", states: ["WA"], mie: 86 }, // GSA: Tacoma, WA
  { names: ["Minneapolis", "St. Paul", "Saint Paul"], country: "US", states: ["MN"], mie: 92 }, // GSA: Minneapolis / St. Paul, MN
  { names: ["Tampa", "St. Petersburg", "Clearwater"], country: "US", states: ["FL"], mie: 80 }, // GSA: Tampa / St. Petersburg, FL
  { names: ["San Diego"], country: "US", states: ["CA"], mie: 86 }, // GSA: San Diego, CA
  { names: ["Denver", "Aurora"], country: "US", states: ["CO"], mie: 92 }, // GSA: Denver / Aurora, CO
  { names: ["Boulder"], country: "US", states: ["CO"], mie: 80 }, // GSA: Boulder / Broomfield, CO
  { names: ["Orlando"], country: "US", states: ["FL"], mie: 80 }, // GSA: Orlando, FL
  { names: ["Charlotte"], country: "US", states: ["NC"], mie: 80 }, // GSA: Charlotte, NC
  { names: ["Baltimore"], country: "US", states: ["MD"], mie: 86 }, // GSA: Baltimore City, MD
  { names: ["St. Louis", "Saint Louis"], country: "US", states: ["MO"], mie: 86 }, // GSA: St. Louis, MO
  { names: ["San Antonio"], country: "US", states: ["TX"], mie: 74 }, // GSA: San Antonio, TX
  { names: ["Austin"], country: "US", states: ["TX"], mie: 80 }, // GSA: Austin, TX
  { names: ["Portland"], country: "US", states: ["OR"], mie: 86 }, // GSA: Portland, OR
  { names: ["Sacramento"], country: "US", states: ["CA"], mie: 86 }, // GSA: Sacramento, CA
  { names: ["Pittsburgh"], country: "US", states: ["PA"], mie: 80 }, // GSA: Pittsburgh, PA
  { names: ["Las Vegas"], country: "US", states: ["NV"], mie: 86 }, // GSA: Las Vegas, NV
  { names: ["Reno"], country: "US", states: ["NV"], mie: 80 }, // GSA: Incline Village / Reno / Sparks, NV
  { names: ["Cincinnati"], country: "US", states: ["OH"], mie: 86 }, // GSA: Cincinnati, OH
  { names: ["Kansas City"], country: "US", states: ["MO", "KS"], mie: 80 }, // GSA: Kansas City, MO
  { names: ["Columbus"], country: "US", states: ["OH"], mie: 80 }, // GSA: Columbus, OH
  { names: ["Cleveland"], country: "US", states: ["OH"], mie: 80 }, // GSA: Cleveland, OH
  { names: ["Indianapolis"], country: "US", states: ["IN"], mie: 80 }, // GSA: Indianapolis / Carmel, IN
  { names: ["Nashville"], country: "US", states: ["TN"], mie: 86 }, // GSA: Nashville, TN
  { names: ["Virginia Beach", "Norfolk"], country: "US", states: ["VA"], mie: 74 }, // GSA: Virginia Beach, VA
  { names: ["Jacksonville"], country: "US", states: ["FL"], mie: 68 }, // GSA: standard CONUS rate (FL)
  { names: ["Providence"], country: "US", states: ["RI"], mie: 80 }, // GSA: Providence / Bristol, RI
  { names: ["Newport"], country: "US", states: ["RI"], mie: 80 }, // GSA: Jamestown / Middletown / Newport, RI
  { names: ["Raleigh"], country: "US", states: ["NC"], mie: 74 }, // GSA: Raleigh, NC
  { names: ["Durham"], country: "US", states: ["NC"], mie: 74 }, // GSA: Durham, NC
  { names: ["Milwaukee"], country: "US", states: ["WI"], mie: 80 }, // GSA: Milwaukee, WI
  { names: ["Oklahoma City"], country: "US", states: ["OK"], mie: 80 }, // GSA: Oklahoma City, OK
  { names: ["Louisville"], country: "US", states: ["KY"], mie: 80 }, // GSA: Louisville, KY
  { names: ["Lexington"], country: "US", states: ["KY"], mie: 80 }, // GSA: Lexington, KY
  { names: ["Memphis"], country: "US", states: ["TN"], mie: 74 }, // GSA: Memphis, TN
  { names: ["Richmond"], country: "US", states: ["VA"], mie: 80 }, // GSA: Richmond, VA
  { names: ["Salt Lake City"], country: "US", states: ["UT"], mie: 80 }, // GSA: Salt Lake City, UT
  { names: ["Park City"], country: "US", states: ["UT"], mie: 92 }, // GSA: Park City, UT
  { names: ["Provo"], country: "US", states: ["UT"], mie: 74 }, // GSA: Provo, UT
  { names: ["Birmingham"], country: "US", states: ["AL"], mie: 80 }, // GSA: Birmingham, AL
  { names: ["Fresno"], country: "US", states: ["CA"], mie: 86 }, // GSA: Fresno, CA
  { names: ["Grand Rapids"], country: "US", states: ["MI"], mie: 80 }, // GSA: Grand Rapids, MI
  { names: ["Ann Arbor"], country: "US", states: ["MI"], mie: 80 }, // GSA: Ann Arbor, MI
  { names: ["Buffalo"], country: "US", states: ["NY"], mie: 80 }, // GSA: Buffalo, NY
  { names: ["Hartford"], country: "US", states: ["CT"], mie: 80 }, // GSA: Hartford, CT
  { names: ["Tucson"], country: "US", states: ["AZ"], mie: 80 }, // GSA: Tucson, AZ
  { names: ["Rochester"], country: "US", states: ["NY"], mie: 80 }, // GSA: Rochester, NY
  { names: ["Tulsa"], country: "US", states: ["OK"], mie: 68 }, // GSA: standard CONUS rate (OK)
  { names: ["Omaha"], country: "US", states: ["NE"], mie: 80 }, // GSA: Omaha, NE
  { names: ["Bridgeport", "Stamford"], country: "US", states: ["CT"], mie: 86 }, // GSA: Bridgeport / Danbury, CT
  { names: ["Greenville"], country: "US", states: ["SC"], mie: 68 }, // GSA: standard CONUS rate (SC)
  { names: ["Albuquerque"], country: "US", states: ["NM"], mie: 80 }, // GSA: Albuquerque, NM
  { names: ["Santa Fe"], country: "US", states: ["NM"], mie: 80 }, // GSA: Santa Fe, NM
  { names: ["Bakersfield"], country: "US", states: ["CA"], mie: 74 }, // GSA: Bakersfield / Ridgecrest, CA
  { names: ["Albany"], country: "US", states: ["NY"], mie: 86 }, // GSA: Albany, NY
  { names: ["Knoxville"], country: "US", states: ["TN"], mie: 74 }, // GSA: Knoxville, TN
  { names: ["El Paso"], country: "US", states: ["TX"], mie: 68 }, // GSA: standard CONUS rate (TX)
  { names: ["Baton Rouge"], country: "US", states: ["LA"], mie: 68 }, // GSA: standard CONUS rate (LA)
  { names: ["New Orleans"], country: "US", states: ["LA"], mie: 80 }, // GSA: New Orleans, LA
  { names: ["McAllen"], country: "US", states: ["TX"], mie: 68 }, // GSA: standard CONUS rate (TX)
  { names: ["New Haven"], country: "US", states: ["CT"], mie: 80 }, // GSA: New Haven, CT
  { names: ["Allentown"], country: "US", states: ["PA"], mie: 68 }, // GSA: standard CONUS rate (PA)
  { names: ["Columbia"], country: "US", states: ["SC"], mie: 74 }, // GSA: Columbia, SC
  { names: ["Sarasota"], country: "US", states: ["FL"], mie: 86 }, // GSA: Sarasota, FL
  { names: ["Dayton"], country: "US", states: ["OH"], mie: 74 }, // GSA: Dayton / Fairborn, OH
  { names: ["Charleston"], country: "US", states: ["SC"], mie: 92 }, // GSA: Charleston, SC
  { names: ["Greensboro"], country: "US", states: ["NC"], mie: 74 }, // GSA: Greensboro, NC
  { names: ["Stockton"], country: "US", states: ["CA"], mie: 74 }, // GSA: Stockton, CA
  { names: ["Fort Myers", "Cape Coral"], country: "US", states: ["FL"], mie: 80 }, // GSA: Fort Myers, FL
  { names: ["Boise"], country: "US", states: ["ID"], mie: 86 }, // GSA: Boise, ID
  { names: ["Colorado Springs"], country: "US", states: ["CO"], mie: 86 }, // GSA: Colorado Springs, CO
  { names: ["Little Rock"], country: "US", states: ["AR"], mie: 68 }, // GSA: standard CONUS rate (AR)
  { names: ["Lakeland"], country: "US", states: ["FL"], mie: 68 }, // GSA: standard CONUS rate (FL)
  { names: ["Akron"], country: "US", states: ["OH"], mie: 68 }, // GSA: standard CONUS rate (OH)
  { names: ["Des Moines"], country: "US", states: ["IA"], mie: 80 }, // GSA: Des Moines, IA
  { names: ["Springfield"], country: "US", states: ["MA"], mie: 74 }, // GSA: Springfield, MA
  { names: ["Poughkeepsie"], country: "US", states: ["NY"], mie: 80 }, // GSA: Poughkeepsie, NY
  { names: ["Ogden"], country: "US", states: ["UT"], mie: 68 }, // GSA: standard CONUS rate (UT)
  { names: ["Madison"], country: "US", states: ["WI"], mie: 80 }, // GSA: Madison, WI
  { names: ["Winston-Salem"], country: "US", states: ["NC"], mie: 68 }, // GSA: standard CONUS rate (NC)
  { names: ["Daytona Beach"], country: "US", states: ["FL"], mie: 80 }, // GSA: Daytona Beach, FL
  { names: ["Syracuse"], country: "US", states: ["NY"], mie: 80 }, // GSA: Syracuse / Oswego, NY
  { names: ["Toledo"], country: "US", states: ["OH"], mie: 68 }, // GSA: standard CONUS rate (OH)
  { names: ["Wichita"], country: "US", states: ["KS"], mie: 68 }, // GSA: standard CONUS rate (KS)
  { names: ["Augusta"], country: "US", states: ["GA"], mie: 74 }, // GSA: Augusta, GA
  { names: ["Palm Bay", "Cocoa Beach"], country: "US", states: ["FL"], mie: 74 }, // GSA: Cocoa Beach, FL
  { names: ["Jackson"], country: "US", states: ["MS"], mie: 68 }, // GSA: standard CONUS rate (MS)
  { names: ["Harrisburg"], country: "US", states: ["PA"], mie: 74 }, // GSA: Harrisburg, PA
  { names: ["Spokane"], country: "US", states: ["WA"], mie: 86 }, // GSA: Spokane, WA
  { names: ["Scranton"], country: "US", states: ["PA"], mie: 68 }, // GSA: standard CONUS rate (PA)
  { names: ["Chattanooga"], country: "US", states: ["TN"], mie: 74 }, // GSA: Chattanooga, TN
  { names: ["Modesto"], country: "US", states: ["CA"], mie: 68 }, // GSA: standard CONUS rate (CA)
  { names: ["Lancaster"], country: "US", states: ["PA"], mie: 74 }, // GSA: Lancaster, PA
  { names: ["Newark", "Jersey City"], country: "US", states: ["NJ"], mie: 86 }, // GSA: Newark, NJ
  { names: ["Savannah"], country: "US", states: ["GA"], mie: 80 }, // GSA: Savannah, GA
  { names: ["Asheville"], country: "US", states: ["NC"], mie: 80 }, // GSA: Asheville, NC
  { names: ["Santa Barbara"], country: "US", states: ["CA"], mie: 92 }, // GSA: Santa Barbara, CA
  { names: ["Monterey", "Carmel", "Carmel-by-the-Sea", "Big Sur"], country: "US", states: ["CA"], mie: 92 }, // GSA: Monterey, CA
  { names: ["Napa", "Yountville", "St. Helena"], country: "US", states: ["CA"], mie: 92 }, // GSA: Napa, CA
  { names: ["Sonoma", "Santa Rosa", "Healdsburg"], country: "US", states: ["CA"], mie: 86 }, // GSA: Santa Rosa, CA
  { names: ["South Lake Tahoe", "Lake Tahoe"], country: "US", states: ["CA"], mie: 86 }, // GSA: South Lake Tahoe, CA
  { names: ["Sedona"], country: "US", states: ["AZ"], mie: 92 }, // GSA: Sedona, AZ
  { names: ["Aspen"], country: "US", states: ["CO"], mie: 92 }, // GSA: Aspen, CO
  { names: ["Vail"], country: "US", states: ["CO"], mie: 92 }, // GSA: Vail, CO
  { names: ["Key West"], country: "US", states: ["FL"], mie: 86 }, // GSA: Key West, FL
  { names: ["Hilton Head"], country: "US", states: ["SC"], mie: 80 }, // GSA: Hilton Head, SC
  { names: ["Myrtle Beach"], country: "US", states: ["SC"], mie: 74 }, // GSA: Myrtle Beach, SC
  { names: ["Burlington"], country: "US", states: ["VT"], mie: 86 }, // GSA: Burlington, VT
  { names: ["Jackson Hole"], country: "US", states: ["WY"], mie: 92 }, // GSA: Jackson / Pinedale, WY
  // Elsewhere, State Department
  { names: ["Toronto"], country: "CA", mie: 144 }, // State: Toronto
  { names: ["Vancouver"], country: "CA", mie: 145 }, // State: Vancouver, seasons weighted by days
  { names: ["Montreal"], country: "CA", mie: 154 }, // State: Montreal
  { names: ["Calgary"], country: "CA", mie: 169 }, // State: Calgary
  { names: ["Ottawa"], country: "CA", mie: 128 }, // State: Ottawa
  { names: ["Quebec City", "Quebec"], country: "CA", mie: 144 }, // State: Quebec
  { names: ["Mexico City"], country: "MX", mie: 132 }, // State: Mexico City, D.F.
  { names: ["Cancún", "Cancun"], country: "MX", mie: 135 }, // State: Cancun
  { names: ["Playa del Carmen"], country: "MX", mie: 98 }, // State: Playa del Carmen, Quintana Roo
  { names: ["Guadalajara"], country: "MX", mie: 126 }, // State: Guadalajara
  { names: ["Monterrey"], country: "MX", mie: 153 }, // State: Monterrey
  { names: ["Los Cabos", "Cabo San Lucas", "San José del Cabo"], country: "MX", mie: 112 }, // State: Los Cabos (Cabo San Lucas and San Jose del Cabo)
  { names: ["Nassau"], country: "BS", mie: 181 }, // State: Nassau
  { names: ["Bogotá"], country: "CO", mie: 193 }, // State: Bogota
  { names: ["Medellín"], country: "CO", mie: 125 }, // State: Medellin
  { names: ["Cartagena"], country: "CO", mie: 190 }, // State: Cartagena
  { names: ["Lima"], country: "PE", mie: 103 }, // State: Lima
  { names: ["Santiago"], country: "CL", mie: 105 }, // State: Santiago
  { names: ["Buenos Aires"], country: "AR", mie: 126 }, // State: Buenos Aires
  { names: ["São Paulo"], country: "BR", mie: 137 }, // State: Sao Paulo
  { names: ["Rio de Janeiro"], country: "BR", mie: 126 }, // State: Rio de Janeiro
  { names: ["Panama City"], country: "PA", mie: 99 }, // State: Panama City
  { names: ["London"], country: "GB", mie: 174 }, // State: London
  { names: ["Edinburgh"], country: "GB", mie: 147 }, // State: Edinburgh, seasons weighted by days
  { names: ["Dublin"], country: "IE", mie: 148 }, // State: Dublin
  { names: ["Paris"], country: "FR", mie: 165 }, // State: Paris
  { names: ["Nice"], country: "FR", mie: 135 }, // State: Nice
  { names: ["Lyon"], country: "FR", mie: 145 }, // State: Lyon
  { names: ["Amsterdam"], country: "NL", mie: 183 }, // State: Amsterdam
  { names: ["Brussels"], country: "BE", mie: 170 }, // State: Brussels
  { names: ["Berlin"], country: "DE", mie: 138 }, // State: Berlin
  { names: ["Munich"], country: "DE", mie: 148 }, // State: Munich
  { names: ["Frankfurt", "Frankfurt am Main"], country: "DE", mie: 133 }, // State: Frankfurt am Main
  { names: ["Hamburg"], country: "DE", mie: 129 }, // State: Hamburg
  { names: ["Vienna"], country: "AT", mie: 139 }, // State: Vienna
  { names: ["Zurich"], country: "CH", mie: 227 }, // State: Zurich
  { names: ["Geneva"], country: "CH", mie: 199 }, // State: Geneva
  { names: ["Prague"], country: "CZ", mie: 109 }, // State: Prague
  { names: ["Budapest"], country: "HU", mie: 105 }, // State: Budapest
  { names: ["Warsaw"], country: "PL", mie: 104 }, // State: Warsaw
  { names: ["Krakow"], country: "PL", mie: 125 }, // State: Krakow
  { names: ["Copenhagen"], country: "DK", mie: 135 }, // State: Copenhagen
  { names: ["Stockholm"], country: "SE", mie: 141 }, // State: Stockholm
  { names: ["Oslo"], country: "NO", mie: 155 }, // State: Oslo
  { names: ["Helsinki"], country: "FI", mie: 137 }, // State: Helsinki
  { names: ["Reykjavik"], country: "IS", mie: 219 }, // State: Reykjavik, seasons weighted by days
  { names: ["Lisbon"], country: "PT", mie: 107 }, // State: Lisbon
  { names: ["Madrid"], country: "ES", mie: 134 }, // State: Madrid
  { names: ["Barcelona"], country: "ES", mie: 128 }, // State: Barcelona
  { names: ["Seville"], country: "ES", mie: 124 }, // State: Seville
  { names: ["Valencia"], country: "ES", mie: 115 }, // State: Valencia
  { names: ["Malaga"], country: "ES", mie: 140 }, // State: Malaga
  { names: ["Rome"], country: "IT", mie: 171 }, // State: Rome
  { names: ["Milan"], country: "IT", mie: 183 }, // State: Milan
  { names: ["Florence"], country: "IT", mie: 130 }, // State: Florence
  { names: ["Venice"], country: "IT", mie: 212 }, // State: Venice
  { names: ["Athens"], country: "GR", mie: 133 }, // State: Athens, seasons weighted by days
  { names: ["Istanbul"], country: "TR", mie: 173 }, // State: Istanbul
  { names: ["Dubai"], country: "AE", mie: 173 }, // State: Dubai
  { names: ["Abu Dhabi"], country: "AE", mie: 149 }, // State: Abu Dhabi
  { names: ["Doha"], country: "QA", mie: 165 }, // State: Doha
  { names: ["Riyadh"], country: "SA", mie: 178 }, // State: Riyadh
  { names: ["Tel Aviv"], country: "IL", mie: 219 }, // State: Tel Aviv
  { names: ["Cairo"], country: "EG", mie: 110 }, // State: Cairo
  { names: ["Marrakesh"], country: "MA", mie: 143 }, // State: Marrakech
  { names: ["Cape Town"], country: "ZA", mie: 92 }, // State: Cape Town
  { names: ["Johannesburg"], country: "ZA", mie: 90 }, // State: Johannesburg
  { names: ["Tokyo"], country: "JP", mie: 122 }, // State: Tokyo City
  { names: ["Kyoto"], country: "JP", mie: 166 }, // State: Kyoto
  { names: ["Osaka", "Kobe"], country: "JP", mie: 197 }, // State: Osaka-Kobe
  { names: ["Seoul"], country: "KR", mie: 132 }, // State: Seoul
  { names: ["Singapore"], country: "SG", mie: 162 }, // State: Singapore
  { names: ["Hong Kong"], country: "HK", mie: 180 }, // State: Hong Kong
  { names: ["Bangkok"], country: "TH", mie: 115 }, // State: Bangkok
  { names: ["Bali", "Denpasar"], country: "ID", mie: 111 }, // State: Bali
  { names: ["Kuala Lumpur"], country: "MY", mie: 84 }, // State: Kuala Lumpur
  { names: ["Shanghai"], country: "CN", mie: 171 }, // State: Shanghai
  { names: ["Beijing"], country: "CN", mie: 147 }, // State: Beijing
  { names: ["Taipei"], country: "TW", mie: 107 }, // State: Taipei
  { names: ["Manila"], country: "PH", mie: 84 }, // State: Manila
  { names: ["Ho Chi Minh City", "Saigon"], country: "VN", mie: 117 }, // State: Ho Chi Minh City
  { names: ["Mumbai"], country: "IN", mie: 105 }, // State: Mumbai, seasons weighted by days
  { names: ["New Delhi", "Delhi"], country: "IN", mie: 113 }, // State: New Delhi
  { names: ["Bengaluru", "Bangalore"], country: "IN", mie: 135 }, // State: Bengaluru
  { names: ["Sydney"], country: "AU", mie: 147 }, // State: Sydney
  { names: ["Melbourne"], country: "AU", mie: 112 }, // State: Melbourne
  { names: ["Brisbane"], country: "AU", mie: 133 }, // State: Brisbane
  { names: ["Auckland"], country: "NZ", mie: 114 }, // State: Auckland
  // Second places with a name listed above. They match only when the destination's country or
  // state says so; a bare name keeps meaning the place listed first.
  { names: ["Portland"], country: "US", states: ["ME"], mie: 80 }, // GSA: Portland, ME
  { names: ["Athens"], country: "US", states: ["GA"], mie: 74 }, // GSA: Athens, GA
];

/** Mean M&IE across each country's listed locations, USD a day, by ISO alpha-2 country. */
const COUNTRY_MIE: Readonly<Record<string, number>> = {
  US: 81, // GSA: mean of 295 non-standard areas
  AE: 157, // State: mean of 3 locations
  AR: 124, // State: mean of 4 locations
  AT: 128, // State: mean of 6 locations
  AU: 123, // State: mean of 15 locations
  BE: 136, // State: mean of 11 locations
  BR: 119, // State: mean of 12 locations
  BS: 138, // State: mean of 5 locations
  CA: 126, // State: mean of 30 locations
  CH: 196, // State: mean of 10 locations
  CL: 103, // State: mean of 2 locations
  CN: 120, // State: mean of 31 locations
  CO: 130, // State: mean of 14 locations
  CZ: 92, // State: mean of 3 locations
  DE: 134, // State: mean of 37 locations
  DK: 121, // State: mean of 5 locations
  EG: 103, // State: mean of 6 locations
  ES: 121, // State: mean of 20 locations
  FI: 128, // State: mean of 2 locations
  FR: 142, // State: mean of 11 locations
  GB: 124, // State: mean of 23 locations
  GR: 126, // State: mean of 3 locations
  HK: 180, // State: mean of 1 locations
  HU: 82, // State: mean of 3 locations
  ID: 86, // State: mean of 9 locations
  IE: 128, // State: mean of 4 locations
  IL: 180, // State: mean of 9 locations
  IN: 102, // State: mean of 11 locations
  IS: 186, // State: mean of 4 locations
  IT: 128, // State: mean of 31 locations
  JP: 115, // State: mean of 26 locations
  KR: 79, // State: mean of 22 locations
  MA: 131, // State: mean of 9 locations
  MX: 109, // State: mean of 42 locations
  MY: 86, // State: mean of 7 locations
  NL: 141, // State: mean of 12 locations
  NO: 162, // State: mean of 4 locations
  NZ: 106, // State: mean of 7 locations
  PA: 81, // State: mean of 4 locations
  PE: 107, // State: mean of 4 locations
  PH: 78, // State: mean of 11 locations
  PL: 109, // State: mean of 8 locations
  PT: 86, // State: mean of 10 locations
  QA: 165, // State: mean of 2 locations
  SA: 133, // State: mean of 6 locations
  SE: 141, // State: mean of 2 locations
  SG: 162, // State: mean of 1 locations
  TH: 96, // State: mean of 13 locations
  TR: 106, // State: mean of 8 locations
  TW: 103, // State: mean of 4 locations
  VN: 108, // State: mean of 6 locations
  ZA: 79, // State: mean of 8 locations
};

const fold = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Each place by {@link placeKey} of every name it goes by. The first listed place wins a shared name. */
const BY_KEY: ReadonlyMap<string, PerDiemPlace> = new Map(
  [...PLACES].reverse().flatMap((place) => place.names.map((name) => [placeKey(name), place] as const))
);

/** Every place under each {@link placeKey}, in listed order, to pick one by country and state. */
const ALL_BY_KEY: ReadonlyMap<string, readonly PerDiemPlace[]> = PLACES.reduce((byKey, place) => {
  for (const name of place.names) byKey.set(placeKey(name), [...(byKey.get(placeKey(name)) ?? []), place]);
  return byKey;
}, new Map<string, PerDiemPlace[]>());

/** Where the planner's pick is: its country, and for the US and Canada its state or province. */
interface Where {
  country: string;
  state: string | null;
}

/** "US-OR" is Oregon, "GR" is Greece; anything else is unknown. */
const parseWhere = (region: string | null | undefined): Where | null => {
  const match = region
    ?.trim()
    .toUpperCase()
    .match(/^([A-Z]{2})(?:-([A-Z0-9]{1,3}))?$/);
  return match ? { country: match[1], state: match[2] ?? null } : null;
};

/** The place a name means: in the given country and state when known, else the first one listed. */
const placeNamed = (name: string, where: Where | null): PerDiemPlace | undefined => {
  const key = placeKey(name);
  if (!where) return BY_KEY.get(key);
  return ALL_BY_KEY.get(key)?.find(
    (place) => place.country === where.country && (!where.state || !place.states || place.states.includes(where.state))
  );
};

/** Folded US state names and codes ("nv", "nevada"), and country names, to the ISO country. */
const COUNTRY_BY_NAME: ReadonlyMap<string, string> = new Map([
  ...Object.entries(US_STATES).flatMap(([code, name]) => [
    [fold(code.slice(3)), "US"] as const,
    [fold(name), "US"] as const,
  ]),
  ...["usa", "us", "united states"].map((name) => [name, "US"] as const),
  ["uk", "GB"] as const,
  ...Object.keys(COUNTRY_MIE).flatMap((code) => {
    const name = getCountryName(code);
    return name ? [[fold(name), code] as const] : [];
  }),
]);

const indexOf = (mie: number): number => Math.round((mie / STANDARD_CONUS_MIE) * COST_INDEX_BASE);

const sourceOf = (country: string): string => (country === "US" ? GSA_PER_DIEM_SOURCE : STATE_PER_DIEM_SOURCE);

const forPlace = (place: PerDiemPlace, name: string): CostIndex => ({
  index: indexOf(place.mie),
  source: `${sourceOf(place.country)}: meals and incidentals $${place.mie} a day`,
  place: name,
});

/** The country a destination names after a comma ("Boise, ID", "Porto, Portugal"), if any. */
const suffixCountry = (city: string): string | undefined => {
  const parts = city.split(",");
  return parts.length > 1 ? COUNTRY_BY_NAME.get(fold(parts[parts.length - 1])) : undefined;
};

/**
 * A destination's cost index from public per diem meal rates, 100 being a place on the standard
 * US rate. Looks up the city, then an area's own cities ("Bay Area" uses San Francisco), then the
 * average of its country's listed locations. With the place's region, a shared name resolves to
 * the right one ("Portland" in "US-ME" is Maine, in "US-OR" Oregon); without it, the name alone
 * decides.
 * @param city - Destination name in any spelling ("Las Vegas", "Ciudad de México", "Porto, Portugal").
 * @param region - Where the planner's pick is, when known: "US-OR" for a US or Canadian city, the
 *   ISO country ("GR") elsewhere.
 * @returns The index, its source and what it is for, or null when neither the city nor its country is known.
 * @throws Never.
 */
export const costIndexFor = (city: string, region?: string | null): CostIndex | null => {
  const name = city.trim();
  const where = parseWhere(region);
  const direct = placeNamed(name, where);
  if (direct) return forPlace(direct, name);
  const area = areaOf(name);
  const member = area?.cities.map((entry) => placeNamed(entry, where)).find(Boolean);
  if (member) return forPlace(member, name);
  const country = where?.country ?? area?.country ?? suffixCountry(name);
  const mie = country ? COUNTRY_MIE[country] : undefined;
  if (!country || mie === undefined) return null;
  return {
    index: indexOf(mie),
    source: `${sourceOf(country)}: mean meals and incidentals of the country's listed locations, $${mie} a day`,
    place: `the ${getCountryName(country) ?? country} average`,
  };
};

/**
 * The basis a city's adjusted rate shows: "adjusted for Las Vegas (index 126)".
 * @param costIndex - The city's cost index.
 * @returns The phrase, lower case so it reads after the rate.
 * @throws Never.
 */
export const describeCostIndex = (costIndex: CostIndex): string => `adjusted for ${costIndex.place} costs`;

/**
 * Which of a city's daily costs follow its cost index, in words: "meeting space and meals
 * adjusted for Las Vegas costs". The index itself stays internal.
 * @param city - One city of the estimate.
 * @returns The phrase, or null when both costs are flat or quoted.
 * @throws Never.
 */
export const cityAdjustment = (city: Pick<CityEstimate, "sources" | "costIndex">): string | null => {
  if (!city.costIndex) return null;
  const items = [
    city.sources.meeting === "adjusted" ? "meeting space" : null,
    city.sources.meals === "adjusted" ? "meals" : null,
  ].filter(Boolean);
  return items.length ? `${items.join(" and ")} ${describeCostIndex(city.costIndex)}` : null;
};
